import {
  ApiEventGet,
  ApiEventSign,
  ApiEventSignItem,
  ApiEventSignReceiveData,
  ReceiveStatusEnum,
  SignRewardTypeEnum,
  SignStartStatusEnum,
  SignStatusEnum,
  SignTypeEnum
} from '@/api/event/type'
import { Base, Component, Prop, Ref } from '@/vue-property-decorator'
import { Bind, Debounce } from '@/utils/Decorator'
import { Button } from '@/plugins/ant-design-vue/tsx-support'
import { GlobalConst } from '@/context'
import { Portal } from 'portal-vue'
import {
  activeReceiveOne,
  canReceiveReward,
  getMosaicValidTips
} from '@/views/event/item/utils'
import { brazilianCurrencyFormatter } from '@/utils/business-utils/finance'
import { showErrorMessageModal } from '@/views/event/item/utils'
import { useEventStore } from '../../../store'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import BusinessUtils from '@/utils/business-utils'
import Modal from '@/controller/Modal'
import QRCode from '@/components/business-components/qr-code'
import RewardDescription from './rewardDescription'
import RewardPattern from './rewardPattern'
import SuccessMsssageModal from './successMsssageModal'
import TooltipsComp from '@/components/business-components/tooltips'
import style from './style.module.scss'

interface Props {
  /** 是充值后弹窗 */
  isAfterRechargeModal?: boolean
  /**
   * 活动详情数据
   */
  event: ApiEventGet
  /**
   * 刷新数据
   */
  fetchData: () => void
  goBack?: () => void
}

interface State {
  isSigning: boolean
}

@Component<EventItemSign>({ name: 'EventItemSign' })
export default class EventItemSign extends Base<State, Props> {
  @Prop()
  isAfterRechargeModal!: Props['isAfterRechargeModal']
  /**
   * 活动详情数据
   */
  @Prop({ required: true })
  event!: Props['event']

  /**
   * 刷新数据
   */
  @Prop({ required: true })
  fetchData!: Props['fetchData']

  /**
   * 返回
   */
  @Prop({ required: true })
  goBack: Props['goBack']

  @Ref()
  public readonly successMsssageModal!: SuccessMsssageModal

  state: State = {
    isSigning: false
  }

  private get isWeb() {
    return useMainStore()?.isWeb
  }

  private get eventStore() {
    return useEventStore()
  }

  /**
   * signDay已经签到天数
   * signDay签到天数
   * 当已签到天数小于 可签到天数是可用
   */
  get buttonValid(): boolean {
    const { signDay, signDays } = this.event.activeData as ApiEventSign
    return (
      signDay > signDays &&
      (this.event.status as number) === SignStartStatusEnum.Normal
    )
  }

  /**
   * 是否禁用按钮
   * 1. 充值 + 打码 是否达到
   * 2. 天数判断及状态判断
   */
  private buttonEnable(btnValid: boolean): boolean {
    const { havePay, needPay, haveBet, needBet } = this.event
      .activeData as ApiEventSign
    const validMap = {
      needPay: havePay >= needPay && btnValid,
      needBet: haveBet >= needBet && btnValid
    }

    if (this.notReceiveStatus) {
      return false
    }

    if (needPay && needBet) {
      return validMap.needPay && validMap.needBet
    }

    // 充值
    if (needPay) {
      return validMap.needPay
    }

    // 打码
    if (needBet) {
      return validMap.needBet
    }

    return btnValid
  }

  /**
   * 是否为累计签到模式
   */
  private get isGrandMode() {
    return (
      (this.event.activeData as ApiEventSign).signType === SignTypeEnum.Grand
    )
  }

  /**
   * 不可领取的状态
   */
  private get notReceiveStatus() {
    const { logStatus } = this.event.activeData as ApiEventSign
    // 不可领取状态
    const notReceiveArr = [
      ReceiveStatusEnum.HasReceive,
      ReceiveStatusEnum.Cancel,
      ReceiveStatusEnum.PendingApply,
      ReceiveStatusEnum.Rejected
    ]
    return notReceiveArr.includes(logStatus)
  }

  @Bind
  @Debounce()
  async handleSign() {
    const { t } = useI18n()
    const mainStore = useMainStore()
    const { receiveId, logStatus } = this.event.activeData as ApiEventSign
    if (this.state.isSigning) {
      return
    }
    if (!mainStore.hasLogined) {
      Modal.openLoginRegisterModal()
      return
    }

    if (this.notReceiveStatus) {
      return
    }

    if (ReceiveStatusEnum.PendingCheck === logStatus) {
      showErrorMessageModal(t('lobby.event.verifyTips') as string)
      return
    }

    if (!canReceiveReward(this.event.receiveDeviceType)) {
      return
    }
    if (receiveId === 0) {
      showErrorMessageModal(t('lobby.event.sign.noSignIn') as string)
      return
    }

    this.setState({
      isSigning: true
    })

    activeReceiveOne({
      params: {
        receiveId: receiveId?.toString(),
        activeId: this.event.id
      },
      onSuccess: (data) => {
        const receiveData = data?.receiveData as ApiEventSignReceiveData
        this.successMsssageModal.open({
          prize: data?.receiveData?.prize ?? '',
          onClose: async () => {
            if (receiveData?.extraPrize > 0) {
              mainStore.audioReceivePlay()
              await this.successMsssageModal.open({
                animation: true,
                extraPrize: receiveData?.extraPrize ?? ''
              })
            } else {
              this.successMsssageModal.close()
            }
          }
        })

        this.eventStore.freshEventCategoryAndActivity()
      },
      judgeResBefore: () => {
        this.setState({ isSigning: false })
        this.fetchData()
      }
    })
  }

  /**
   * 展示app下载二维码
   */
  private renderQRCode() {
    const { errorMessage } = getMosaicValidTips(this.event.receiveDeviceType)
    return (
      <div>
        <div>{errorMessage}</div>
        <QRCode />
      </div>
    )
  }

  private getReceiveText(statas: number) {
    const { t } = useI18n()
    return {
      [ReceiveStatusEnum.Processing]: t('lobby.event.sign.signIn'),
      [ReceiveStatusEnum.CanReceive]: t('lobby.event.sign.hasSignIn'),
      [ReceiveStatusEnum.HasReceive]: t('lobby.modal.task.distributed'),
      [ReceiveStatusEnum.Expired]: t('lobby.modal.task.expired'),
      [ReceiveStatusEnum.Cancel]: t('lobby.modal.task.cancel'),
      [ReceiveStatusEnum.PendingCheck]: t('lobby.event.sign.signIn'),
      [ReceiveStatusEnum.PendingApply]: t('lobby.event.pendingApply'),
      [ReceiveStatusEnum.Rejected]: t('lobby.records.rejected')
    }[statas] as string
  }

  /**
   * 领取文案
   */
  private get receiveText() {
    const { t } = useI18n()
    const { logStatus } = this.event.activeData as ApiEventSign
    const isNormal =
      (this.event.status as number) !== SignStartStatusEnum.Normal
    let text = ''

    if (isNormal) {
      return t('lobby.event.sign.signIn') as string
    }

    if (this.buttonValid || ReceiveStatusEnum.Processing !== logStatus) {
      text = this.getReceiveText(logStatus)
    } else {
      text = t('lobby.event.sign.hasSignIn') as string
    }
    return text
  }

  /**
   * 签到信息
   * 签到方式： 连续签到 + 累计签到
   * @returns
   */
  renderSummary() {
    const { t } = useI18n()
    const { signDays, needPay, havePay, receiveReward, needBet, haveBet } = this
      .event.activeData as ApiEventSign
    // /* 1. 未开始或者过期，均显示签到文案。 2. 当正常签到时，根据状态判断是否已签到*/
    // const text =
    //   this.buttonValid ||
    //   (this.event.status as number) === SignStartStatusEnum.UnStart ||
    //   (this.event.status as number) === SignStartStatusEnum.End
    //     ? t('lobby.event.sign.signIn')
    //     : t('lobby.event.sign.hasSignIn')

    return (
      <div class={style.summary}>
        <div class={style.info}>
          <div>
            <inner-html
              class={[style.item]}
              type="div"
              text={t(
                `lobby.event.sign.${this.isGrandMode ? 'grand' : 'continued'}`,
                {
                  x: signDays
                }
              ).toString()}
            />
            <inner-html
              class={[style.item]}
              type="div"
              text={t('lobby.event.sign.collected', {
                x: brazilianCurrencyFormatter(receiveReward || 0)
              }).toString()}
            />
          </div>
          {(!!needBet || !!needPay) && (
            <div class={style.receiveRight}>
              {!!needPay && (
                <inner-html
                  class={[style.item]}
                  type="div"
                  text={t('lobby.event.sign.topupNeed', {
                    x: brazilianCurrencyFormatter(havePay),
                    y: brazilianCurrencyFormatter(needPay)
                  }).toString()}
                />
              )}
              {!!needBet && (
                <inner-html
                  class={[style.item]}
                  type="div"
                  text={t('lobby.event.sign.betNeed', {
                    x: brazilianCurrencyFormatter(haveBet),
                    y: brazilianCurrencyFormatter(needBet)
                  }).toString()}
                />
              )}
            </div>
          )}
        </div>

        <Portal
          to={
            this.isAfterRechargeModal
              ? GlobalConst.PortalName.AfterRechargeActivityBottom
              : GlobalConst.PortalName.ActivityBottom
          }
        >
          <div class={[style.bottomBtns, 'daily-bonus-bottom-btns']}>
            {!this.isWeb && !this.isAfterRechargeModal && this.goBack && (
              <Button.Tsx
                class={['H5BottomReturnBtn']}
                onClick={() => this.goBack?.()}
              >
                <span>{t('lobby.common.tips.return')}</span>
              </Button.Tsx>
            )}
            <Button.Tsx
              type="success"
              class={style.button}
              onClick={this.handleSign}
              disabled={!this.buttonEnable(this.buttonValid)}
            >
              {this.receiveText}
            </Button.Tsx>
          </div>
        </Portal>
      </div>
    )
  }

  /**
   * 签到列表项
   */
  private renderItem(item: ApiEventSignItem) {
    const { t } = useI18n()
    const { isWeb } = useMainStore()

    const tooltipsCompPositionTop = isWeb
      ? BusinessUtils.px2rem(-10)
      : BusinessUtils.px2rem(-15)

    const tooltipsCompPositionRight = isWeb
      ? BusinessUtils.px2rem(-25)
      : BusinessUtils.px2rem(-15)

    const hasSign = item.signStatus === SignStatusEnum.SignIn
    const itemCanClick =
      (this.event.status as number) === SignStartStatusEnum.Normal &&
      !hasSign &&
      item.day <= (this.event.activeData as ApiEventSign).signDay

    return (
      <div
        class={[style.item, 'sign-item']}
        style={{
          cursor:
            this.buttonEnable(itemCanClick) && !this.notReceiveStatus
              ? 'pointer'
              : 'default'
        }}
        onClick={() => {
          this.buttonEnable(itemCanClick) && this.handleSign()
        }}
      >
        {item?.extraPrize ? (
          <TooltipsComp
            charge={1}
            top={tooltipsCompPositionTop}
            right={tooltipsCompPositionRight}
            text={<span>{t('lobby.event.sign.extraPrize')}</span>}
          />
        ) : null}

        <div class={style.caption}>
          {t('lobby.event.sign.dayCount', {
            x: item.day,
            X: item.day
          })}
        </div>
        <RewardPattern hasSign={hasSign} chestUrl={item.icon}></RewardPattern>
        <RewardDescription
          hasSign={hasSign}
          text={
            hasSign
              ? brazilianCurrencyFormatter(item.prize)
              : (item.rewardType === SignRewardTypeEnum.Fixed &&
                  brazilianCurrencyFormatter(item.prize)) ||
                item.text
          }
        ></RewardDescription>
      </div>
    )
  }

  /**
   * 签到列表
   * @returns
   */
  private renderList() {
    return (
      <div class={[style.list, 'sign-list']}>
        {((this.event.activeData as ApiEventSign).signList || []).map(
          this.renderItem
        )}
      </div>
    )
  }

  render() {
    return (
      <div class={[style.container, 'sign-box']}>
        {this.renderSummary()}
        {this.renderList()}
        <SuccessMsssageModal ref={'successMsssageModal'} />
      </div>
    )
  }
}
